import { BigNum, SpotMarketConfig } from '@drift-labs/sdk';
import React, { useCallback, useEffect, useState } from 'react';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import { useTransferCollateral } from 'src/hooks/driftActions/spot-management/useTransferCollateral';
import useAccountsBeingLiquidated from 'src/hooks/Liquidations/useAccountsBeingLiquidated';
import useAccountTargetSpotBalance, {
	AccountTargetSpotBalance,
} from 'src/hooks/useAccountTargetSpotBalance';
import useDriftAccountStore, {
	AccountData,
} from 'src/stores/useDriftAccountsStore';
import useCollateralModalState from 'src/components/Modals/CollateralModal/hooks/useCollateralModalState';
import { CollateralModalProps } from 'src/components/Modals/CollateralModal/types';
import { MAIN_POOL_ID } from '@drift/common';
import { CollateralInputV2 } from '../../Inputs/CollateralInput';
import { InputQuickActions } from '../../Inputs/InputQuickActions';
import Button from '../../primitives/Button/Button';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { toNotional } from 'src/utils/formatting';
import useTickedAllSubAccounts from 'src/hooks/useTickedAllSubAccounts';
import useUnsettledPnl from 'src/components/Modals/CollateralModal/hooks/useUnsettledPnl';
import useModal from 'src/components/Modals/hooks/useModal';
import BaseModal from 'src/components/primitives/BaseModal/BaseModal';
import { CollateralSourceSelector } from 'src/components/Inputs/CollateralSourceSelector';
import Divider from 'src/components/primitives/Divider/Divider';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import TextButton from 'src/components/primitives/TextButton/TextButton';

type SpotBalance = AccountTargetSpotBalance;

const TransferModal = ({
	collateralIndex,
	collateralPoolId,
	targetAccountKey,
	defaultLargestBalance,
}: CollateralModalProps) => {
	const { transferCollateral } = useTransferCollateral();
	const { captureEvent } = usePostHogCapture();
	const isMobile = useIsMobileScreenSize();
	const { hide, show } = useModal();

	const availableSubAccounts = useTickedAllSubAccounts().filter(
		(account) => account.client?.getUserAccount().poolId === MAIN_POOL_ID
	);

	const {
		updateCollateralIndex,
		handleTargetAccountChange,
		selectedCollateralMarket,
		selectedTargetAccountKey,
		selectedPoolId,
	} = useCollateralModalState({
		collateralIndex,
		collateralPoolId,
		targetAccountKey,
		defaultLargestBalance,
	});

	const currentUserKey = useDriftAccountStore((s) => s.currentUserKey);
	const currentAccountPoolId = useDriftAccountStore(
		(s) =>
			s.accounts[currentUserKey ?? '']?.client?.getUserAccount().poolId ?? 0
	);

	const [submitting, setSubmitting] = useState(false);
	const [collatTransferAmount, setCollatTransferAmount] = useState('');
	const [isMax, setIsMax] = useState(false);
	const [isMaxRepay, setIsMaxRepay] = useState(false);

	const [donorAccountSelection, setDonorAccountSelection] = useState<string>(
		selectedTargetAccountKey ?? currentUserKey ?? ''
	);

	const [recipientAccountSelection, setRecipientAccountSelection] =
		useState<string>();

	const accountsBeingLiquidated = useAccountsBeingLiquidated();
	const selectedAccountCanbeLiqd = accountsBeingLiquidated.find(
		(account) => account.userKey === donorAccountSelection
	);

	const noSecondaryAccount = availableSubAccounts.length <= 1;
	const sameDonorAndRecipient =
		recipientAccountSelection === donorAccountSelection;

	const donorBalances = useAccountTargetSpotBalance(
		selectedCollateralMarket,
		donorAccountSelection,
		true
	);

	const recipientBalances = useAccountTargetSpotBalance(
		selectedCollateralMarket,
		recipientAccountSelection,
		true
	);

	const collatAmountBignum = collatTransferAmount
		? BigNum.fromPrint(
				collatTransferAmount,
				selectedCollateralMarket.precisionExp
			)
		: BigNum.zero(selectedCollateralMarket.precisionExp);
	const exceededMax = donorBalances.withdrawLimitBase.lt(collatAmountBignum);
	const allowConfirmation =
		!submitting &&
		!sameDonorAndRecipient &&
		!!collatTransferAmount &&
		collatAmountBignum.gt(BigNum.zero()) &&
		!!recipientAccountSelection &&
		!exceededMax &&
		!selectedAccountCanbeLiqd &&
		!noSecondaryAccount &&
		selectedPoolId === MAIN_POOL_ID &&
		currentAccountPoolId === MAIN_POOL_ID;

	const handleTransfer = useCallback(async () => {
		if (!recipientAccountSelection) return;
		setSubmitting(true);

		captureEvent('submitted_transfer');

		try {
			// Clamp to max withdrawable to avoid over-transfer attempts
			const amountToSend = collatAmountBignum.gt(
				donorBalances.withdrawLimitBase
			)
				? donorBalances.withdrawLimitBase
				: collatAmountBignum;

			const result = await transferCollateral(
				amountToSend,
				selectedCollateralMarket,
				donorAccountSelection,
				recipientAccountSelection
			);
			if (result === true) hide();
		} finally {
			setSubmitting(false);
		}
	}, [
		captureEvent,
		collatAmountBignum,
		selectedCollateralMarket,
		donorAccountSelection,
		recipientAccountSelection,
		hide,
		donorBalances.withdrawLimitBase,
	]);

	const handleValueChange = useCallback(
		(newValue?: string, maxRepay = false) => {
			setIsMax(false);
			setIsMaxRepay(maxRepay);

			// Set input to max amount if exceeds limit
			if (!newValue || newValue === '') {
				setCollatTransferAmount(newValue ?? '');
				return;
			}

			const inputBigNum = BigNum.fromPrint(
				newValue,
				selectedCollateralMarket.precisionExp
			);
			if (inputBigNum.lte(donorBalances.withdrawLimitBase)) {
				setCollatTransferAmount(newValue);
			} else {
				// Set to max amount if input exceeds limit
				setCollatTransferAmount(donorBalances.withdrawLimitBase.print());
				setIsMax(true);
			}
		},
		[selectedCollateralMarket.precisionExp, donorBalances.withdrawLimitBase]
	);

	const handleMax = useCallback(() => {
		setCollatTransferAmount(donorBalances.withdrawLimitBase.print());
		setIsMax(true);
	}, [donorBalances.withdrawLimitBase]);

	const handleDonorAccountChange = useCallback(
		(accountKey: string) => {
			setDonorAccountSelection(accountKey);
			handleTargetAccountChange(accountKey);
			setCollatTransferAmount('');
			setIsMax(false);
			setIsMaxRepay(false);
		},
		[handleTargetAccountChange]
	);

	const handleRecipientAccountChange = useCallback((accountKey: string) => {
		setRecipientAccountSelection(accountKey);
		setCollatTransferAmount('');
		setIsMax(false);
		setIsMaxRepay(false);
	}, []);

	// Initialize or update recipient account selection when donor or list changes
	useEffect(() => {
		if (!availableSubAccounts.length) return;

		const firstOther = availableSubAccounts.find(
			(account) => account.userKey !== donorAccountSelection
		)?.userKey;

		// If current recipient is undefined or equals donor, set to first available other account
		if (
			!recipientAccountSelection ||
			recipientAccountSelection === donorAccountSelection
		) {
			if (firstOther) {
				setRecipientAccountSelection(firstOther);
				setIsMax(false);
				setIsMaxRepay(false);
			}
		}
	}, [availableSubAccounts, donorAccountSelection]);

	// Reset transfer amount if market selection changes
	useEffect(() => {
		setCollatTransferAmount('');
	}, [selectedCollateralMarket?.symbol]);

	// Reset form when switching donor/recipient accounts
	useEffect(() => {
		setCollatTransferAmount('');
		setIsMax(false);
		setIsMaxRepay(false);
	}, [donorAccountSelection, recipientAccountSelection]);

	// Adjust amount if exceeded max
	useEffect(() => {
		if (exceededMax && isMax) {
			setCollatTransferAmount(donorBalances.withdrawLimitBase.print());
		}
	}, [exceededMax, isMax, donorBalances.withdrawLimitBase]);

	// Sync with collateral modal state
	useEffect(() => {
		if (selectedTargetAccountKey) {
			setDonorAccountSelection(selectedTargetAccountKey);
			// When donor changes via modal state, ensure recipient remains a different account
			const firstOther = availableSubAccounts.find(
				(account) => account.userKey !== selectedTargetAccountKey
			)?.userKey;
			if (
				!recipientAccountSelection ||
				recipientAccountSelection === selectedTargetAccountKey
			) {
				if (firstOther) {
					setRecipientAccountSelection(firstOther);
					setIsMax(false);
					setIsMaxRepay(false);
				}
			}
		}
	}, [selectedTargetAccountKey, availableSubAccounts]);

	const showNewAccountModal = () => {
		show({ type: 'new_subaccount', props: {} });
	};

	return (
		<BaseModal.Container onClose={hide} dataTestId="transfer-modal">
			<BaseModal.Title onClose={hide} closeButtonTestId="transfer-modal-close">
				Transfer
			</BaseModal.Title>
			<BaseModal.Body>
				{recipientBalances?.netBaseBalance.isNeg() && (
					<span>
						If you have open borrows, transfers of the same asset type will
						automatically repay the borrowed amount.
					</span>
				)}

				{availableSubAccounts.length === 0 && (
					<InlineAlert
						variant="warning"
						title="No Drift accounts. Create an account by depositing to enable transfers."
					/>
				)}

				{(selectedPoolId !== MAIN_POOL_ID ||
					currentAccountPoolId !== MAIN_POOL_ID) && (
					<InlineAlert
						variant="warning"
						title="Transfers are not available to or from isolated pool subaccounts."
					/>
				)}

				<AccountSelectors
					donorAccountSelection={donorAccountSelection}
					recipientAccountSelection={recipientAccountSelection}
					availableSubAccounts={availableSubAccounts}
					handleDonorAccountChange={handleDonorAccountChange}
					handleRecipientAccountChange={handleRecipientAccountChange}
					noSecondaryAccount={noSecondaryAccount}
					showNewAccountModal={showNewAccountModal}
				/>

				{!noSecondaryAccount && (
					<>
						<CollateralInputV2
							maxAmount={donorBalances.withdrawLimitBase}
							selectedMarket={selectedCollateralMarket}
							onChangeValue={handleValueChange}
							onChangeMarket={(val) => updateCollateralIndex(val.marketIndex)}
							value={
								isMax
									? donorBalances.withdrawLimitBase.print()
									: collatTransferAmount
							}
							label="Assets and amount"
							source={{
								type: 'userAccount',
								userKey: donorAccountSelection,
							}}
							defaultToHighestBalance
							customElementRight={
								<InputQuickActions.SizePresets
									onClick={(amount) => {
										if (amount.eq(donorBalances.withdrawLimitBase)) {
											handleMax();
										} else {
											setIsMax(false);
											setCollatTransferAmount(amount.print());
										}
									}}
									maxAmount={donorBalances.withdrawLimitBase}
								/>
							}
							poolId={selectedPoolId}
							disabled={selectedPoolId !== MAIN_POOL_ID}
							fullScreenPopover={isMobile}
							fullScreenPopoverTitle="Select Asset"
						/>

						<RepayBorrowingSection
							recipientBalances={recipientBalances}
							onAmountClick={(amount, isMaxRepay = false) => {
								handleValueChange(amount.print(), isMaxRepay);
								if (amount.eq(donorBalances.withdrawLimitBase)) {
									setIsMax(true);
								}
							}}
						/>

						<TransferUnsettledAmountMessage
							donorAccountKey={donorAccountSelection}
						/>
						{selectedAccountCanbeLiqd && (
							<span className="text-text-negative-red-button">
								Transferring out is disabled during liquidation. Please try
								again later.
							</span>
						)}

						<Divider />

						<AssetBalanceSection
							donorBalances={donorBalances}
							recipientBalances={recipientBalances}
							selectedCollatBank={selectedCollateralMarket}
							donorAccountSelection={donorAccountSelection}
							recipientAccountSelection={recipientAccountSelection}
							availableSubAccounts={availableSubAccounts}
							collatTransferAmountBigNum={collatAmountBignum}
							isMaxRepay={isMaxRepay}
						/>
					</>
				)}
			</BaseModal.Body>
			<BaseModal.Footer>
				<Button
					className="w-full"
					size="lg"
					onClick={handleTransfer}
					disabled={!allowConfirmation}
					dataTestId="transfer-modal-submit"
				>
					{submitting ? 'Confirming...' : 'Confirm'}
				</Button>
			</BaseModal.Footer>
		</BaseModal.Container>
	);
};

const TransferUnsettledAmountMessage = ({
	donorAccountKey,
}: {
	donorAccountKey: string;
}) => {
	const { hasUnsettled, unsettledNumber, handleSettleNow } =
		useUnsettledPnl(donorAccountKey);

	if (!hasUnsettled) return null;

	return (
		<div className="flex items-center gap-x-xs">
			<span className="typo-b4 text-text-secondary">
				You have an unsettled amount of {toNotional(unsettledNumber)}.
			</span>
			<TextButton size="sm" variant="tertiary" onClick={handleSettleNow}>
				Settle now
			</TextButton>
		</div>
	);
};

const AccountSelectors = ({
	donorAccountSelection,
	recipientAccountSelection,
	availableSubAccounts,
	handleDonorAccountChange,
	handleRecipientAccountChange,
	noSecondaryAccount,
	showNewAccountModal,
}: {
	donorAccountSelection: string;
	recipientAccountSelection: string | undefined;
	availableSubAccounts: AccountData[];
	handleDonorAccountChange: (accountKey: string) => void;
	handleRecipientAccountChange: (accountKey: string) => void;
	noSecondaryAccount: boolean;
	showNewAccountModal: () => void;
}) => {
	return (
		<>
			<CollateralSourceSelector
				label="Transfer from"
				onChange={handleDonorAccountChange}
				options={availableSubAccounts}
				initialSelection={donorAccountSelection}
			/>

			{noSecondaryAccount ? (
				<div className="flex flex-col gap-y-sm">
					<div className="rounded-sm border border-input-border bg-input-bg p-md typo-b4 text-text-secondary">
						No secondary accounts. Add an account to transfer assets to it.
					</div>
					<Button size="md" onClick={showNewAccountModal}>
						Add Account
					</Button>
				</div>
			) : (
				<CollateralSourceSelector
					label="Transfer to"
					onChange={handleRecipientAccountChange}
					options={availableSubAccounts.filter(
						(account) => account.userKey !== donorAccountSelection
					)}
					initialSelection={recipientAccountSelection}
					includeDelegates
				/>
			)}
		</>
	);
};

const RepayBorrowingSection = ({
	recipientBalances,
	onAmountClick,
}: {
	recipientBalances: SpotBalance;
	onAmountClick: (amount: BigNum, isMaxRepay?: boolean) => void;
}) => {
	if (!recipientBalances?.netBaseBalance.isNeg()) {
		return null;
	}

	const borrowedAmount = recipientBalances.netBaseBalance.abs();

	return (
		<div className="flex w-full items-start justify-between">
			<span className="typo-b3">Repay Borrowing</span>
			<InputQuickActions.SizePresets
				onClick={(amount) => {
					const isMaxRepay = amount.eq(borrowedAmount);
					onAmountClick(amount, isMaxRepay);
				}}
				maxAmount={borrowedAmount}
			/>
		</div>
	);
};

const AssetBalanceSection = ({
	donorBalances,
	recipientBalances,
	selectedCollatBank,
	donorAccountSelection,
	recipientAccountSelection,
	availableSubAccounts,
	collatTransferAmountBigNum,
	isMaxRepay,
}: {
	donorBalances: SpotBalance;
	recipientBalances: SpotBalance;
	selectedCollatBank: SpotMarketConfig;
	donorAccountSelection: string;
	recipientAccountSelection: string | undefined;
	availableSubAccounts: AccountData[];
	collatTransferAmountBigNum: BigNum;
	isMaxRepay: boolean;
}) => {
	const donorAccountName = (() => {
		const account = availableSubAccounts.find(
			(acc) => acc.userKey === donorAccountSelection
		);

		return account?.name || 'Account';
	})();

	const recipientAccountName = (() => {
		const account = availableSubAccounts.find(
			(acc) => acc.userKey === recipientAccountSelection
		);

		return account?.name || 'Account';
	})();

	// Calculate projected balances after transfer
	const projectedDonorBalance = donorBalances.netBaseBalance.sub(
		collatTransferAmountBigNum
	);
	const projectedRecipientBalance = isMaxRepay
		? BigNum.zero(selectedCollatBank.precisionExp)
		: recipientBalances.netBaseBalance.add(collatTransferAmountBigNum);

	return (
		<div className="flex flex-col gap-y-sm">
			<div className="flex w-full items-start justify-between">
				<span>{donorAccountName}</span>
				<span className="text-text-default">
					{projectedDonorBalance.prettyPrint()} {selectedCollatBank.symbol}
				</span>
			</div>

			{recipientAccountSelection && (
				<div className="flex w-full items-start justify-between">
					<span>{recipientAccountName}</span>
					<span className="text-text-default">
						{projectedRecipientBalance.prettyPrint()}{' '}
						{selectedCollatBank.symbol}
					</span>
				</div>
			)}
		</div>
	);
};

export default React.memo(TransferModal);
